import { useEffect, useMemo, useState } from "react";
import { api } from "../api";
import { PageSection, Pagination, ReportTable, StatusBadge } from "../components/reporting";
import type { Column } from "../components/reporting";

interface AuditEvent {
  id: string;
  tenantId: string;
  tenantName: string;
  tenantSlug: string;
  actorName: string;
  action: string;
  subjectType: string;
  subjectId: string | null;
  occurredAt: string;
}

const columns: Column[] = [
  { label: "When", align: "left", width: "180px" },
  { label: "Actor", align: "left", width: "180px" },
  { label: "Tenant", align: "left", width: "190px" },
  { label: "Action", align: "left", width: "230px" },
  { label: "Opaque subject", align: "left" },
];

export function AdminAudit() {
  const [events, setEvents] = useState<AuditEvent[]>([]);
  const [tenantId, setTenantId] = useState("all");
  const [query, setQuery] = useState("");
  const [error, setError] = useState("");
  useEffect(() => {
    api<{ items: AuditEvent[] }>("/api/admin/audit")
      .then((response) => setEvents(response.items))
      .catch((problem) =>
        setError(problem instanceof Error ? problem.message : "Audit unavailable"),
      );
  }, []);
  const tenants = useMemo(
    () => [
      ...new Map(
        events.map((event) => [event.tenantId, { id: event.tenantId, name: event.tenantName }]),
      ).values(),
    ],
    [events],
  );
  const filtered = events.filter(
    (event) =>
      (tenantId === "all" || event.tenantId === tenantId) &&
      (!query.trim() || event.action.toLowerCase().includes(query.toLowerCase())),
  );
  const rows = filtered.map((event) => [
    <span className="text-body text-secondary whitespace-nowrap">
      {new Date(event.occurredAt).toLocaleString("en-GB")}
    </span>,
    <span className="text-body text-primary">{event.actorName}</span>,
    <span>
      <span className="block text-body text-primary">{event.tenantName}</span>
      <span className="block font-mono text-micro text-tertiary">{event.tenantSlug}</span>
    </span>,
    <span className="font-mono text-caption text-primary">{event.action}</span>,
    <span className="font-mono text-caption text-tertiary">
      {event.subjectType}
      {event.subjectId ? ` · ${event.subjectId}` : ""}
    </span>,
  ]);

  return (
    <div className="flex flex-col gap-[24px]">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Filter by action"
          className="h-9 w-[280px] px-3 rounded-xs border border-default bg-surface-white text-body text-primary"
        />
        <select
          value={tenantId}
          onChange={(event) => setTenantId(event.target.value)}
          className="h-9 px-3 rounded-xs border border-default bg-surface-white text-caption text-secondary"
        >
          <option value="all">All tenants</option>
          {tenants.map((tenant) => (
            <option key={tenant.id} value={tenant.id}>
              {tenant.name}
            </option>
          ))}
        </select>
      </div>
      <PageSection
        title={`${filtered.length} audit events`}
        right={
          <span className="inline-flex items-center gap-2 text-caption text-tertiary">
            <StatusBadge label="Tenant-scoped" tone="blue" />
            No lead PII in this view
          </span>
        }
        bodyClassName="px-[20px] pt-[16px] pb-[20px]"
      >
        {error ? (
          <p className="py-12 text-center text-status-red">{error}</p>
        ) : rows.length ? (
          <>
            <ReportTable columns={columns} rows={rows} />
            <Pagination from={1} to={rows.length} total={rows.length} />
          </>
        ) : (
          <p className="py-12 text-center text-body text-tertiary">No audit events match.</p>
        )}
      </PageSection>
    </div>
  );
}
